import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Lock,
  RefreshCw,
  ShieldAlert,
  Users,
} from 'lucide-react';
import {
  applyParameters,
  detectParameters,
  ParameterError,
  type SessionUser,
} from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { pickForm } from '../lib/chart';
import { BarChart, KpiRow, LineChart, StatTile } from '../components/dashboard/Charts';
import { DataTable } from '../components/dashboard/DataTable';
import { ParameterBar } from '../components/dashboard/ParameterBar';
import { ExportDialog } from '../components/ExportDialog';
import { FullPageSpinner } from '../components/FullPageSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { dashboardGate } from '../lib/query-gate';
import { useConsoleStore } from '../store/console.store';

/**
 * One dashboard, at full size.
 *
 * The card on the index answers "what is the number"; this answers "and which
 * rows is it made of", which is the question anyone asks second. So the same
 * result is shown twice — drawn at the top, enumerated below — rather than
 * making someone re-run the query in the console to see the detail.
 *
 * Filters come from the literals already written into the saved query. That is
 * what keeps this honest: the reader is adjusting the query's own values, not
 * composing SQL, and what runs is still a single statement through the same
 * guard, limits, masking and audit trail as everything else.
 */
export function DashboardDetailPage({ user }: { user: SessionUser }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const saved = useQuery({ queryKey: ['saved-queries'], queryFn: api.savedQueries.list });
  const datasources = useQuery({ queryKey: ['datasources'], queryFn: api.datasources.list });

  const query = saved.data?.queries.find((q) => q.id === id);

  const parameters = useMemo(() => (query ? detectParameters(query.sql) : []), [query]);

  /** Edits in the filter bar, before Apply. */
  const [draft, setDraft] = useState<Record<string, string>>({});
  /** What the currently displayed result was run with. */
  const [applied, setApplied] = useState<Record<string, string>>({});
  const [paramError, setParamError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [runToken, setRunToken] = useState(0);

  const clientId = useRef(crypto.randomUUID());

  const dirty = parameters.some(
    (p) => (draft[p.id] ?? p.value) !== (applied[p.id] ?? p.value),
  );

  // What actually runs: the saved statement with the applied values written
  // back into it. Computed rather than stored, so it can never drift from the
  // values the controls are showing.
  const effectiveSql = useMemo(() => {
    if (!query) return '';
    try {
      return applyParameters(query.sql, parameters, applied);
    } catch {
      // Guarded at Apply; falling back keeps the page rendering.
      return query.sql;
    }
  }, [query, parameters, applied]);

  const run = useQuery({
    queryKey: ['dashboard-detail', id, effectiveSql, runToken],
    enabled: Boolean(query) && user.permissions.includes('query:run'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
    retry: (attempt, error) =>
      attempt < 2 && error instanceof ApiError && error.code === 'QUERY_LIMIT_EXCEEDED',
    retryDelay: 1500,
    queryFn: () =>
      dashboardGate(() =>
        api.query.execute({
          dataSourceId: query!.dataSourceId,
          sql: effectiveSql,
          clientQueryId: `${clientId.current}-${runToken}-${Object.values(applied).join('|')}`,
        }),
      ),
  });

  const apply = () => {
    if (!query) return;
    try {
      // Validate by attempting the rewrite. Failing here means the reader sees
      // why, instead of the database rejecting something they cannot see.
      applyParameters(query.sql, parameters, draft);
      setParamError(null);
      setApplied({ ...draft });
    } catch (error) {
      setParamError(
        error instanceof ParameterError ? error.message : 'Those values cannot be used.',
      );
    }
  };

  const reset = () => {
    setDraft({});
    setApplied({});
    setParamError(null);
  };

  const openInConsole = () => {
    const store = useConsoleStore.getState();
    const tabId = store.addTab(effectiveSql);
    store.renameTab(tabId, query!.name);
    navigate('/console');
  };

  if (saved.isLoading) return <FullPageSpinner label="Loading the dashboard…" />;

  if (!query) {
    return (
      <div className="p-8">
        <EmptyState
          icon={ShieldAlert}
          title="That dashboard is not here"
          body="It may have been deleted, or it belongs to someone who has not shared it."
          action={
            <Link className="btn btn-primary" to="/dashboards">
              Back to dashboards
            </Link>
          }
        />
      </div>
    );
  }

  const connection = datasources.data?.datasources.find((d) => d.id === query.dataSourceId);
  const form = run.data?.status === 'SUCCESS' ? pickForm(run.data) : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-edge bg-surface-raised px-4 py-2">
        <Link
          className="btn btn-ghost px-1.5"
          to="/dashboards"
          aria-label="Back to dashboards"
          title="Back to dashboards"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
        </Link>

        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h1 className="truncate text-sm font-semibold text-ink">{query.name}</h1>
            {query.shared && (
              <span className="chip border-edge bg-surface-sunken text-ink-faint">
                <Users className="h-2.5 w-2.5" />
                shared by {query.ownerName}
              </span>
            )}
          </div>
          <p className="truncate text-2xs text-ink-muted">
            {query.description || connection?.name || ''}
            {query.description && connection?.name ? ` · ${connection.name}` : ''}
          </p>
        </div>

        <div className="flex-1" />

        <button
          className="btn"
          onClick={() => setRunToken((n) => n + 1)}
          disabled={run.isFetching}
        >
          <RefreshCw className={run.isFetching ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
          {run.isFetching ? 'Running…' : 'Refresh'}
        </button>

        {user.permissions.includes('export:csv') && run.data?.status === 'SUCCESS' && (
          <button className="btn" onClick={() => setExporting(true)}>
            <Download className="h-3 w-3" />
            Export
          </button>
        )}

        <button className="btn" onClick={openInConsole}>
          <ExternalLink className="h-3 w-3" />
          Open in console
        </button>
      </header>

      {/* The page is the scroller. Children keep their natural height so a
          chart never squeezes the table below it. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <ParameterBar
          dataSourceId={query.dataSourceId}
          parameters={parameters}
          values={draft}
          onChange={(pid, value) => setDraft((d) => ({ ...d, [pid]: value }))}
          onApply={apply}
          onReset={reset}
          dirty={dirty}
          running={run.isFetching}
          error={paramError}
        />

        {run.isLoading && (
          <div className="panel mb-3 h-52 animate-pulse rounded-lg" aria-label="Running the query" />
        )}

        {run.error != null && <RunError error={run.error} />}

        {run.data && run.data.status !== 'SUCCESS' && (
          <div className="mb-3 rounded-lg border border-danger/30 bg-danger/5 p-3">
            <p className="text-2xs leading-relaxed text-danger">
              {run.data.error?.message ?? `The query ${run.data.status.toLowerCase()}.`}
            </p>
          </div>
        )}

        {/* The picture, when the shape names one. A table-shaped result skips
            straight to the table below rather than showing it twice. */}
        {run.data && form && form.kind !== 'table' && form.kind !== 'empty' && (
          <section className="panel mb-3 shrink-0 rounded-lg p-4" style={{ containerType: 'inline-size' }}>
            <div className={form.kind === 'bar' ? 'h-72' : 'h-44'}>
              {form.kind === 'stat' && (
                <StatTile label={form.label} value={form.value} masked={form.masked} />
              )}
              {form.kind === 'kpi' && <KpiRow items={form.items} />}
              {form.kind === 'bar' && <BarChart points={form.points} measure={form.measure} />}
              {form.kind === 'line' && <LineChart points={form.points} measure={form.measure} />}
            </div>
            {form.kind === 'bar' && form.truncatedTo !== null && (
              <p className="mt-2 text-2xs text-ink-faint">
                Showing the top {form.points.length} of {form.truncatedTo} — every row is in the
                table below.
              </p>
            )}
          </section>
        )}

        {run.data?.status === 'SUCCESS' && <DataTable result={run.data} />}
      </div>

      {exporting && run.data && (
        <ExportDialog
          queryRunId={run.data.queryRunId}
          rowCount={run.data.rowCount}
          onClose={() => setExporting(false)}
        />
      )}
    </div>
  );
}

function RunError({ error }: { error: unknown }) {
  const denied = error instanceof ApiError && error.code === 'POLICY_DENIED';
  const message = error instanceof ApiError ? error.message : 'The query could not be run.';

  return (
    <div
      className={
        denied
          ? 'mb-3 rounded-lg border border-lock/30 bg-lock/5 p-3'
          : 'mb-3 rounded-lg border border-danger/30 bg-danger/5 p-3'
      }
    >
      <div className="flex items-start gap-2">
        {denied ? (
          <Lock className="mt-px h-3.5 w-3.5 shrink-0 text-lock" />
        ) : (
          <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0 text-danger" />
        )}
        <p className={denied ? 'text-2xs leading-relaxed text-lock' : 'text-2xs leading-relaxed text-danger'}>
          {message}
        </p>
      </div>
    </div>
  );
}
